import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PWAInstallModal from '../components/PWAInstallModal';

const STORAGE_KEY = 'crm_pwa_install_dismissed';

const estaEmModoApp = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

const ehIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// localStorage LANCA em aba anonima ou com armazenamento bloqueado.
const dispensouParaSempre = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const PWAInstallContext = createContext(null);

/**
 * Estado de instalacao do PWA, no topo do app.
 *
 * Ficava dentro da HomePage, o que criava dois problemas: o convite so podia aparecer
 * naquela tela, e nao havia como oferecer "Instalar" em nenhum outro lugar — quem
 * perdesse o modal automatico nao tinha mais caminho nenhum pela interface.
 *
 * O registro do service worker saiu daqui e foi para o boot (`src/index.js`): ele e
 * pre-requisito de instalabilidade e precisa existir ANTES do login, senao o Chrome nem
 * dispara `beforeinstallprompt`.
 */
export function PWAInstallProvider({ children }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [instalado, setInstalado] = useState(estaEmModoApp);

    const isIOSDevice = useMemo(ehIOS, []);

    useEffect(() => {
        if (estaEmModoApp()) return undefined;

        // O convite automatico respeita o "Nao mostrar novamente", mas o PROMPT em si e
        // guardado de qualquer jeito: ele alimenta o item manual do menu, que o usuario
        // pediu explicitamente ao clicar.
        const abrirSePermitido = () => {
            if (!dispensouParaSempre()) setShowModal(true);
        };

        const capturar = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            abrirSePermitido();
        };

        // Evento que disparou antes do React montar (ponte no index.html).
        if (window.__pwaInstallPrompt) {
            setDeferredPrompt(window.__pwaInstallPrompt);
            window.__pwaInstallPrompt = null;
            abrirSePermitido();
        }

        window.addEventListener('beforeinstallprompt', capturar);

        const aoInstalar = () => {
            setInstalado(true);
            setDeferredPrompt(null);
            setShowModal(false);
        };
        window.addEventListener('appinstalled', aoInstalar);

        // iOS nao tem `beforeinstallprompt` em navegador nenhum — la o modal so mostra
        // as instrucoes manuais.
        let timer;
        if (isIOSDevice && !dispensouParaSempre()) {
            timer = window.setTimeout(() => setShowModal(true), 2500);
        }

        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('beforeinstallprompt', capturar);
            window.removeEventListener('appinstalled', aoInstalar);
        };
    }, [isIOSDevice]);

    const triggerInstall = useCallback(async () => {
        if (!deferredPrompt) return false;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        // O prompt e de uso unico: aceito ou recusado, nao pode ser reaproveitado.
        setDeferredPrompt(null);
        if (outcome === 'accepted') {
            setInstalado(true);
            return true;
        }
        return false;
    }, [deferredPrompt]);

    const dismissModal = useCallback((permanente = false) => {
        setShowModal(false);
        if (permanente) {
            try {
                localStorage.setItem(STORAGE_KEY, 'true');
            } catch {
                /* segue sem memoria */
            }
        }
    }, []);

    const abrirModal = useCallback(() => setShowModal(true), []);

    // Ha o que oferecer? No Android depende do prompt guardado; no iOS sempre ha (as
    // instrucoes manuais). Em modo app ja instalado, nunca.
    const podeInstalar = !instalado && (!!deferredPrompt || isIOSDevice);

    const valor = useMemo(
        () => ({
            showModal,
            instalado,
            isIOSDevice,
            podeInstalar,
            triggerInstall,
            dismissModal,
            abrirModal,
        }),
        [
            showModal,
            instalado,
            isIOSDevice,
            podeInstalar,
            triggerInstall,
            dismissModal,
            abrirModal,
        ]
    );

    return (
        <PWAInstallContext.Provider value={valor}>
            {children}
            {/* O modal vive aqui, e nao na Home: assim o item "Instalar aplicativo" do
                menu funciona de qualquer tela. */}
            <PWAInstallModal
                open={showModal}
                isIOSDevice={isIOSDevice}
                onInstall={triggerInstall}
                onDismiss={dismissModal}
            />
        </PWAInstallContext.Provider>
    );
}

export function usePWAInstall() {
    const ctx = useContext(PWAInstallContext);
    if (!ctx) {
        throw new Error('usePWAInstall precisa estar dentro de <PWAInstallProvider>.');
    }
    return ctx;
}
